import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  RawAxiosRequestHeaders
} from 'axios'
import axios from 'axios'
import { environment } from '../../config/environment'
import { logger } from '../../utils/logger'
import { apiClient } from './apiClient'

// Same base URL as other platform calls (dev.services). Packages backend expects ISTIO_MESH + Authentik token.
const API_URLS = {
  local: 'http://localhost:3000',
  staging: 'https://dev.services.leadconnectorhq.com'
}

const getBaseUrl = (): string => {
  const isLocal =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      process.env.NODE_ENV === 'development')
  return isLocal ? API_URLS.local : API_URLS.staging
}

const DEFAULT_HEADERS: RawAxiosRequestHeaders = {
  'Content-Type': 'application/json',
  Accept: 'application/json',
  version: '2021-04-15',
  'source-id': '123',
  channel: 'ISTIO_MESH',
  'istio-workload-name': 'platform-devtools-frontend'
}

function handleError(error: unknown): Error {
  if (axios.isAxiosError(error) && error.response) {
    const message =
      error.response.data?.message || error.response.data?.error || 'An error occurred'
    const error_code = error.response.data?.error_code
    const customError = new Error(message)
    ;(customError as Error & { code?: string }).code = error_code
    return customError
  }
  if (axios.isAxiosError(error) && error.request) {
    return new Error('No response from server. Please check your internet connection.')
  }
  if (error instanceof Error) {
    return error
  }
  return new Error('An unexpected error occurred')
}

/**
 * Dedicated API client for packages on dev.services.
 * Sends ISTIO_MESH headers and Authentik Bearer token.
 * Uses apiClient.getValidToken() for proactive refresh and 401 retry (shared auth behavior).
 */
class PackagesApiClient {
  private client: AxiosInstance
  private baseUrl: string

  constructor() {
    this.baseUrl = getBaseUrl()
    this.client = axios.create({
      baseURL: this.baseUrl,
      timeout: environment.api.timeout,
      headers: DEFAULT_HEADERS
    })

    this.client.interceptors.request.use(
      async (config) => {
        config.headers = Object.assign({}, DEFAULT_HEADERS, config.headers)
        const token = await apiClient.getValidToken()
        if (token && String(token).trim()) {
          config.headers.Authorization = `Bearer ${token.trim()}`
        }
        const requestUrl =
          typeof config.url === 'string' && config.url.startsWith('http')
            ? config.url
            : `${config.baseURL || this.baseUrl}${config.url || ''}`
        logger.log('Packages API request (ISTIO_MESH + auth):', requestUrl)
        return config
      },
      (error) => {
        throw error
      }
    )

    this.client.interceptors.response.use(
      (response) => {
        if (response.data && Object.prototype.hasOwnProperty.call(response.data, 'data')) {
          response.data = response.data.data
        }
        return response
      },
      async (error) => {
        const originalRequest = error.config
        const is401 = error.response?.status === 401
        if (is401 && originalRequest && !originalRequest._retry) {
          originalRequest._retry = true
          try {
            const newToken = await apiClient.getValidToken()
            if (newToken?.trim()) {
              originalRequest.headers = originalRequest.headers || {}
              originalRequest.headers.Authorization = `Bearer ${newToken.trim()}`
              return await this.client.request(originalRequest)
            }
          } catch {
            // ignore; fall through to handleError
          }
        }
        throw handleError(error)
      }
    )
  }

  private async request<T>(config: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.request(config)
    return response.data
  }

  public async get<T>(
    url: string,
    params?: unknown,
    headers?: RawAxiosRequestHeaders
  ): Promise<T> {
    return this.request<T>({ method: 'GET', url, params, headers })
  }

  public async post<T>(
    url: string,
    data?: unknown,
    headers?: RawAxiosRequestHeaders
  ): Promise<T> {
    return this.request<T>({ method: 'POST', url, data, headers })
  }

  public async put<T>(
    url: string,
    data?: unknown,
    headers?: RawAxiosRequestHeaders
  ): Promise<T> {
    return this.request<T>({ method: 'PUT', url, data, headers })
  }

  public async patch<T>(
    url: string,
    data?: unknown,
    headers?: RawAxiosRequestHeaders
  ): Promise<T> {
    return this.request<T>({ method: 'PATCH', url, data, headers })
  }

  public async delete<T>(url: string, headers?: RawAxiosRequestHeaders): Promise<T> {
    return this.request<T>({ method: 'DELETE', url, headers })
  }
}

export const packagesApiClient = new PackagesApiClient()
